import React, {Component} from 'react';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import {DESTINATION_DATA} from '@/proptypes/index';
import {connect} from 'react-redux';
import {Helmet} from 'react-helmet-async';
import {withRouter} from 'react-router-dom';
import {compose} from 'redux';
import dayjs from 'utils/dayjs-timezone';
import BranchUtils from 'utils/branch';
import withConfig from '@/config/withConfig';

const MetaType = Object.freeze({
    AIRPORT: 'airport',
    CITY: 'city',
    DESTINATION: 'destination',
    VENUE: 'venue',
    EVENT: 'event',
    OTHER: 'other',
    PARKING_NEAR_ME: 'parking-near-me',
});

export class SearchMeta extends Component {
    static propTypes = {
        isMobile: PropTypes.bool.isRequired,
        siteUrl: PropTypes.string.isRequired,
        requestedPath: PropTypes.string.isRequired,
        displayDateFormat: PropTypes.string.isRequired,
        standardTimeFormat: PropTypes.string.isRequired,
        parkingNearMeTitle: PropTypes.string,
        parkingNearMeDescription: PropTypes.string,
        searchType: PropTypes.string,
        isMonthly: PropTypes.bool,
        searchRequestLatitude: PropTypes.number,
        searchRequestLongitude: PropTypes.number,
        starts: PropTypes.string,
        ends: PropTypes.string,
        searchString: PropTypes.string,
        city: PropTypes.object,
        event: PropTypes.object,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        pageType: PropTypes.string,
        hideCanonical: PropTypes.bool,
    };

    constructor(props) {
        super(props);

        const {
            isMobile,
            siteUrl,
            requestedPath,
            displayDateFormat,
            standardTimeFormat,
            parkingNearMeTitle,
            parkingNearMeDescription,
            searchType,
            pageType,
            isMonthly,
            searchRequestLatitude,
            searchRequestLongitude,
            starts,
            ends,
            searchString,
            event,
            city,
            destination,
        } = props;

        const hasCityData = !isEmpty(city);
        const hasDestinationData = !isEmpty(destination);
        const hasEventData = !isEmpty(event);
        const hasAirportData = !isEmpty(destination.airport);
        const isCitySearch = searchType === 'city';
        const isEventSearch = searchType === 'event';
        const isDestinationSearch = searchType === 'destination';
        const isAirportSearch = isDestinationSearch && hasAirportData;
        const isVenueSearch = searchType === 'venue';
        const isVenueDestinationEventSearch =
            isVenueSearch || isDestinationSearch || isEventSearch;
        const isParkingNearMe = requestedPath.includes('parking-near-me');

        function getMetaType() {
            if (isEventSearch && hasEventData) {
                return MetaType.EVENT;
            } else if (isAirportSearch || hasAirportData) {
                return MetaType.AIRPORT;
            } else if (isDestinationSearch && hasDestinationData) {
                return MetaType.DESTINATION;
            } else if (isVenueSearch && hasDestinationData) {
                return MetaType.VENUE;
            } else if (isCitySearch && hasCityData) {
                return MetaType.CITY;
            } else if (isParkingNearMe) {
                return MetaType.PARKING_NEAR_ME;
            } else {
                return MetaType.OTHER;
            }
        }

        const metaType = getMetaType();

        this.ctx = {
            metaType,

            parkingNearMeTitle,
            parkingNearMeDescription,

            latitude:
                isVenueDestinationEventSearch && hasDestinationData
                    ? destination.latitude
                    : isCitySearch && hasCityData
                    ? city.latitude
                    : searchRequestLatitude,
            longitude:
                isVenueDestinationEventSearch && hasDestinationData
                    ? destination.longitude
                    : isCitySearch && hasCityData
                    ? city.longitude
                    : searchRequestLongitude,

            searchString,
            isMobile,

            starts,
            ends,

            ...(hasEventData && {
                eventStartDay: dayjs(event.starts).format(displayDateFormat),
                eventStartTime: dayjs(event.starts).format(standardTimeFormat),
            }),

            city,
            destination,
            event,

            isEventSearch,
            isDestinationSearch,
            isMonthly,
            isVenueSearch,
            isCitySearch,

            hasDestinationData,
            hasEventData,
            hasCityData,

            siteUrl,

            cityLabel: hasCityData ? city.display_name : 'SpotHero',
            hasDeepLinks: isVenueDestinationEventSearch || isCitySearch,

            pageType,
            isParkingNearMe,
        };
    }

    getCanonical() {
        const {
            metaType,
            siteUrl,
            event,
            isMonthly,
            destination,
            city,
        } = this.ctx;

        if (!isMonthly) {
            switch (metaType) {
                case MetaType.AIRPORT:
                    return `${siteUrl}/${MetaType.AIRPORT}/${
                        city.slug
                    }/${destination.airport.iata_code.toLowerCase()}-parking`;
                case MetaType.DESTINATION:
                case MetaType.VENUE:
                case MetaType.EVENT:
                    return `${siteUrl}/${MetaType.DESTINATION}/${city.slug}/${destination.relative_url}`;
                case MetaType.CITY:
                    return `${siteUrl}/${MetaType.CITY}/${city.relative_url}`;
                case MetaType.OTHER:
                default: {
                    if (metaType !== MetaType.PARKING_NEAR_ME) return null;

                    return `${siteUrl}/parking-near-me`;
                }
            }
        }

        switch (metaType) {
            case MetaType.EVENT: {
                return `${siteUrl}/${event.search_url}`;
            }
            case MetaType.AIRPORT:
            case MetaType.VENUE:
            case MetaType.DESTINATION: {
                const {
                    relative_url: relativeUrl,
                    parent_url: parentUrl,
                } = destination;

                return `${siteUrl}/${MetaType.DESTINATION}/monthly/${
                    city.slug
                }/${parentUrl || relativeUrl}`;
            }
            case MetaType.CITY: {
                return `${siteUrl}/${metaType}/monthly/${city.relative_url}`;
            }
            case MetaType.OTHER:
            default: {
                if (metaType !== MetaType.PARKING_NEAR_ME) return null;

                return `${siteUrl}/monthly-parking-near-me`;
            }
        }
    }

    getTitle() {
        const {
            metaType,
            event,
            isMonthly,
            destination,
            hasCityData,
            city,
            cityLabel,
            eventStartDay,
        } = this.ctx;

        switch (metaType) {
            case MetaType.EVENT: {
                return !isEmpty(event.meta_title)
                    ? event.meta_title
                    : `${event.title} Parking (${eventStartDay}) | SpotHero`;
            }
            case MetaType.AIRPORT:
            case MetaType.DESTINATION: {
                return isMonthly && destination.monthly_enabled
                    ? `${destination.title} Monthly Parking - Reserve & Save | SpotHero`
                    : !isEmpty(destination.meta_title)
                    ? destination.meta_title
                    : `${destination.title} Parking - Reserve & Save | SpotHero`;
            }
            case MetaType.VENUE: {
                return !isEmpty(destination.meta_title)
                    ? destination.meta_title
                    : `${destination.title} Parking, Find a ${
                          hasCityData ? `${city.display_name}` : ''
                      } Parking Garage near ${destination.title}.`;
            }
            case MetaType.CITY: {
                return `${city.display_name} Parking - Save Up to 50% | SpotHero`;
            }
            case MetaType.PARKING_NEAR_ME:
                return 'Find parking near you | Book now on SpotHero';
            case MetaType.OTHER:
            default: {
                return isMonthly
                    ? `${cityLabel} Monthly Parking | SpotHero`
                    : `${cityLabel} Parking - Save Up to 50% | SpotHero`;
            }
        }
    }

    getDescription() {
        const {
            metaType,
            event,
            destination,
            isMonthly,
            hasCityData,
            city,
            cityLabel,
            eventStartDay,
            eventStartTime,
        } = this.ctx;

        switch (metaType) {
            case MetaType.EVENT: {
                return !isEmpty(event.meta_description)
                    ? event.meta_description
                    : `Find parking near ${destination.title} for ${event.title} on ${eventStartDay} at ${eventStartTime}.`;
            }
            case MetaType.AIRPORT:
            case MetaType.DESTINATION: {
                return isMonthly && destination.monthly_enabled
                    ? `Find great ${destination.title} monthly parking through SpotHero. Shop for monthly parking in ${destination.title} and book the best rates with SpotHero.`
                    : `Reserve ${destination.title} parking through SpotHero. Book parking near ${destination.title} and save with SpotHero.`;
            }
            case MetaType.VENUE: {
                return `${
                    destination.title
                } parking options. Use SpotHero to search for parking spots in lots and garages near ${
                    hasCityData ? `${city.display_name}'s` : ''
                } ${destination.title}.`;
            }
            case MetaType.CITY: {
                return `${city.display_name} Parking - Find, compare & reserve parking near ${city.display_name}. Book guaranteed parking online & save with SpotHero.`;
            }
            case MetaType.PARKING_NEAR_ME:
                return 'Find, book, and save on parking with convenient garages, lots, and valets near you. Find parking near work, home, airports, and events with the help of SpotHero.';
            case MetaType.OTHER:
            default: {
                return isMonthly
                    ? `Find ${cityLabel} monthly parking. Search for garages & lots using our parking map. Get up to 50% off when you book online with SpotHero.`
                    : `${cityLabel} Parking - Find, compare & reserve parking. Book guaranteed parking online & save with SpotHero.`;
            }
        }
    }

    getExtraSchema(canonicalUrl) {
        const {metaType, siteUrl, city, destination} = this.ctx;

        switch (metaType) {
            case MetaType.EVENT: {
                return {
                    '@context': 'http://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            item: {
                                '@id': `${siteUrl}/${city.relative_url}`,
                                '@type': 'City',
                                name: city.display_name,
                                url: `${siteUrl}/${city.relative_url}`,
                            },
                        },
                    ],
                };
            }
            case MetaType.DESTINATION: {
                return {
                    '@context': 'http://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            item: {
                                '@id': `${siteUrl}/${city.relative_url}`,
                                '@type': 'City',
                                name: `${city.display_name}`,
                                url: `${siteUrl}/${city.relative_url}`,
                            },
                        },
                    ],
                };
            }
            case MetaType.VENUE: {
                return [
                    {
                        '@context': 'http://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            {
                                '@type': 'ListItem',
                                position: 1,
                                item: {
                                    '@id': `${siteUrl}/${city.relative_url}`,
                                    '@type': 'City',
                                    name: `${city.display_name}`,
                                    url: `${siteUrl}/${city.relative_url}`,
                                },
                            },
                        ],
                    },
                    {
                        '@context': 'http://schema.org',
                        '@type': 'Place',
                        url: `${siteUrl}/${city.slug}/${destination.relative_url}`,
                        name: `${destination.title}`,
                        address: {
                            '@type': 'PostalAddress',
                            streetAddress: `${destination.street_address}`,
                            addressLocality: `${destination.city}`,
                            addressRegion: `${destination.state}`,
                        },
                        geo: {
                            '@type': 'GeoCoordinates',
                            latitude: `${destination.latitude}`,
                            longitude: `${destination.longitude}`,
                        },
                        containedInPlace: {
                            '@type': 'City',
                            name: `${city.display_name}`,
                            url: `${siteUrl}/${city.relative_url}`,
                        },
                    },
                ];
            }
            case MetaType.CITY: {
                return null;
            }
            case MetaType.OTHER:
            default: {
                return {
                    '@context': 'http://schema.org',
                    '@type': 'BreadcrumbList',
                    itemListElement: [
                        {
                            '@type': 'ListItem',
                            position: 1,
                            item: {
                                '@id': `${canonicalUrl}`,
                                '@type': 'City',
                                name: `${city.display_name}`,
                                url: `${canonicalUrl}`,
                            },
                        },
                    ],
                };
            }
        }
    }

    getDeepLinkage() {
        const {
            metaType,
            latitude,
            longitude,
            searchString,
            hasDestinationData,
            destination,
            starts,
            ends,
            event,
            isMobile,
        } = this.ctx;

        const place =
            hasDestinationData && destination.google_places
                ? destination.google_places[0]
                : null;

        if (isMobile) {
            const nativeAppDeepLink = BranchUtils.generateDeepLink({
                latitude,
                longitude,
                searchString,
                place,

                ...(metaType === MetaType.EVENT
                    ? {starts: event.starts, ends: event.ends}
                    : {starts, ends}),
            });

            BranchUtils.setViewData({
                data: {$canonical_url: nativeAppDeepLink}, // eslint-disable-line camelcase
            });
        }

        return BranchUtils.generateDeepLinkParams({
            latitude,
            longitude,
            searchString,
            place,
        });
    }

    render() {
        const {hideCanonical} = this.props;
        const {hasDestinationData, isParkingNearMe, hasDeepLinks} = this.ctx;
        const mobileDeepLinkParams = this.getDeepLinkage();
        const canonicalUrl = this.getCanonical();
        const title = this.getTitle();
        const description = this.getDescription();
        const extraSchema = this.getExtraSchema(canonicalUrl);
        // only parking near me pages are indexed for SEO
        const isNoIndex = !isParkingNearMe;

        return (
            <>
                <Helmet>
                    {title && <title>{title}</title>}
                    {description && (
                        <meta
                            name="description"
                            itemProp="description"
                            content={description}
                        />
                    )}
                    {canonicalUrl && !hideCanonical && (
                        <link rel="canonical" href={canonicalUrl} />
                    )}
                    {isNoIndex && (
                        <meta name="robots" content="noindex, follow" />
                    )}
                    {hasDestinationData && !hideCanonical && (
                        <meta property="al:web:url" content={canonicalUrl} />
                    )}
                    {extraSchema && !hideCanonical && (
                        <script type="application/ld+json">
                            {JSON.stringify(extraSchema)}
                        </script>
                    )}
                </Helmet>
                {hasDeepLinks && (
                    <Helmet>
                        <link
                            rel="alternate"
                            href={`android-app://com.spothero.spothero/spothero/com.spothero/search/${mobileDeepLinkParams}`}
                        />
                        <link
                            rel="alternate"
                            href={`ios-app://499097243/spothero/search/${mobileDeepLinkParams}`}
                        />
                        <meta
                            property="al:ios:url"
                            content={`spothero://search${mobileDeepLinkParams}`}
                        />
                        <meta
                            property="al:ios:app_store_id"
                            content="499097243"
                        />
                        <meta property="al:ios:app_name" content="SpotHero" />
                        <meta
                            property="al:android:url"
                            content={`spothero://search${mobileDeepLinkParams}`}
                        />
                        <meta
                            property="al:android:app_name"
                            content="SpotHero"
                        />
                        <meta
                            property="al:android:package"
                            content="com.spothero.spothero"
                        />
                    </Helmet>
                )}
            </>
        );
    }
}

const mapStateToProps = (
    state,
    {
        history: {
            location: {pathname: requestedPath},
        },
    }
) => {
    const {
        search: {
            data: {searchType},
        },
        searchRequest: {
            pageType,
            monthly: isMonthly,
            latitude: searchRequestLatitude,
            longitude: searchRequestLongitude,
            starts,
            ends,
            search_string: searchString,
        },
        city: {data: city},
        destination: {data: destination},
        event: {data: event},
    } = state;

    return {
        searchType,
        pageType,
        isMonthly,
        requestedPath,
        searchRequestLatitude,
        searchRequestLongitude,
        starts,
        ends,
        searchString,
        event,
        city,
        destination,
    };
};

const enhance = compose(
    withConfig([
        'displayDateFormat',
        'isMobile',
        'siteUrl',
        'standardTimeFormat',
    ]),
    withRouter,
    connect(mapStateToProps)
);

export default enhance(SearchMeta);
